import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getConversationTemplate } from "@samklang/content";
import type { AnnanRole, ParticipantResponse, Perspective } from "@samklang/schemas";
import type { SummaryItem } from "@samklang/conversation-engine";
import { perspectiveLabel, formatDate } from "@/features/kartlaggning/lib";

export interface PerspectiveRun {
  readonly perspective: Perspective;
  readonly role?: AnnanRole;
  readonly status: "planned" | "in_progress" | "done";
  readonly conductedBy: string;
  readonly conductedWith: string;
  readonly registeredBy: string;
  readonly startedAt?: string;
  readonly finishedAt?: string;
  readonly formVersion: number;
  readonly sessionId?: string;
  readonly responses?: Readonly<Record<string, ParticipantResponse>>;
  readonly summary?: readonly SummaryItem[];
}

export interface KartlaggningAction {
  readonly id: string;
  readonly description: string;
  readonly purpose: string;
  readonly responsible: string;
  readonly startDate: string;
  readonly followUpDate: string;
  readonly followUpMethod: string;
  readonly linkedNeed: string;
  readonly status: "planned" | "in_progress" | "done" | "cancelled";
}

export interface KartlaggningFollowUp {
  readonly id: string;
  readonly perspective: Perspective;
  readonly role?: AnnanRole;
  readonly at: string;
  readonly value: number;
  readonly comment: string;
  readonly conductedBy: string;
}

export interface AccessLogEntry {
  readonly at: string;
  readonly actor: string;
  readonly action: string;
  readonly subject: string;
}

export interface Kartlaggning {
  readonly id: string;
  readonly templateId: string;
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly participantReference: string;
  readonly createdBy: string;
  readonly runs: readonly PerspectiveRun[];
  readonly actions: readonly KartlaggningAction[];
  readonly reusedSessionNotes: readonly string[];
  readonly followUps: readonly KartlaggningFollowUp[];
  readonly accessLog: readonly AccessLogEntry[];
}

export interface CreateKartlaggningInput {
  readonly templateId: string;
  readonly participantReference: string;
  readonly createdBy: string;
  readonly perspectives: readonly Perspective[];
  readonly conductedWith?: string;
  readonly annanRole?: AnnanRole;
}

interface KartlaggningStore {
  readonly kartlaggningar: readonly Kartlaggning[];
  listKartlaggningar: () => readonly Kartlaggning[];
  getKartlaggning: (id: string) => Kartlaggning | undefined;
  createKartlaggning: (input: CreateKartlaggningInput) => string;
  startRun: (kartlaggningId: string, perspective: Perspective) => PerspectiveRun;
  saveRunProgress: (kartlaggningId: string, perspective: Perspective, responses: Readonly<Record<string, ParticipantResponse>>) => void;
  completeRun: (kartlaggningId: string, perspective: Perspective, responses: Readonly<Record<string, ParticipantResponse>>, summary: readonly SummaryItem[]) => void;
  setRunProvenance: (kartlaggningId: string, perspective: Perspective, values: { readonly conductedBy: string; readonly conductedWith: string; readonly registeredBy: string; readonly role?: AnnanRole }) => void;
  addAction: (kartlaggningId: string, action: Omit<KartlaggningAction, "id">) => void;
  updateAction: (kartlaggningId: string, actionId: string, patch: Partial<Omit<KartlaggningAction, "id">>) => void;
  updateActionStatus: (kartlaggningId: string, actionId: string, status: KartlaggningAction["status"]) => void;
  removeAction: (kartlaggningId: string, actionId: string) => void;
  addFollowUp: (kartlaggningId: string, followUp: Omit<KartlaggningFollowUp, "id" | "at">) => void;
  addAccessLog: (kartlaggningId: string, entry: Omit<AccessLogEntry, "at">) => void;
  addReusedSessionNote: (kartlaggningId: string, note: string) => void;
  reusePreviousKartlaggning: (kartlaggningId: string, sourceId: string) => void;
  deleteKartlaggning: (id: string) => void;
  resetAll: () => void;
}

function createId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `kartlaggning-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Appends an access-log entry to the matching kartläggning. */
function withLog(kartlaggningar: readonly Kartlaggning[], kartlaggningId: string, entry: Omit<AccessLogEntry, "at">): readonly Kartlaggning[] {
  return kartlaggningar.map((kartlaggning) =>
    kartlaggning.id === kartlaggningId
      ? {
          ...kartlaggning,
          updatedAt: new Date().toISOString(),
          accessLog: [...kartlaggning.accessLog, { ...entry, at: new Date().toISOString() }],
        }
      : kartlaggning,
  );
}

export const useKartlaggningStore = create<KartlaggningStore>()(
  persist(
    (set, get) => ({
      kartlaggningar: [],

      listKartlaggningar: () => get().kartlaggningar,

      getKartlaggning: (id) => get().kartlaggningar.find((kartlaggning) => kartlaggning.id === id),

      createKartlaggning: ({ templateId, participantReference, createdBy, perspectives, conductedWith, annanRole }) => {
        const template = getConversationTemplate(templateId);
        if (!template) throw new Error("Kartläggningsmallen finns inte.");
        const id = createId();
        const now = new Date().toISOString();
        const runs: PerspectiveRun[] = perspectives.map((perspective) => {
          const role: AnnanRole | undefined = perspective === "annan" ? annanRole ?? "specialpedagog" : undefined;
          return {
            perspective,
            ...(role !== undefined ? { role } : {}),
            status: "planned",
            conductedBy: createdBy,
            conductedWith: conductedWith ?? "",
            registeredBy: createdBy,
            formVersion: template.version,
          };
        });
        const kartlaggning: Kartlaggning = {
          id,
          templateId,
          title: template.title,
          createdAt: now,
          updatedAt: now,
          participantReference,
          createdBy,
          runs,
          actions: [],
          reusedSessionNotes: [],
          followUps: [],
          accessLog: [{ at: now, actor: createdBy, action: "skapad", subject: `Kartläggning ${template.title} skapad` }],
        };
        set((current) => ({ kartlaggningar: [...current.kartlaggningar, kartlaggning] }));
        return id;
      },

      startRun: (kartlaggningId, perspective) => {
        const kartlaggning = get().getKartlaggning(kartlaggningId);
        if (!kartlaggning) throw new Error("Kartläggningen finns inte.");
        const template = getConversationTemplate(kartlaggning.templateId);
        if (!template) throw new Error("Kartläggningsmallen finns inte.");
        const now = new Date().toISOString();
        const existing = kartlaggning.runs.find((candidate) => candidate.perspective === perspective);
        if (existing?.status === "done") throw new Error("Detta perspektiv är redan genomfört.");
        const baseRun: PerspectiveRun = existing
          ? existing
          : {
              perspective,
              ...(perspective === "annan" ? { role: "specialpedagog" as AnnanRole } : {}),
              status: "planned",
              conductedBy: kartlaggning.createdBy,
              conductedWith: kartlaggning.runs[0]?.conductedWith ?? "",
              registeredBy: kartlaggning.createdBy,
              formVersion: template.version,
            };
        const nextRun: PerspectiveRun = {
          ...baseRun,
          status: "in_progress",
          startedAt: baseRun.startedAt ?? now,
          sessionId: baseRun.sessionId ?? createId(),
          formVersion: template.version,
        };
        set((current) => ({
          kartlaggningar: current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  updatedAt: now,
                  runs: candidate.runs.map((candidateRun) => (candidateRun.perspective === perspective ? nextRun : candidateRun)),
                }
              : candidate,
          ),
        }));
        return nextRun;
      },

      saveRunProgress: (kartlaggningId, perspective, responses) => {
        set((current) => ({
          kartlaggningar: current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  updatedAt: new Date().toISOString(),
                  runs: candidate.runs.map((run) => (run.perspective === perspective ? { ...run, responses } : run)),
                }
              : candidate,
          ),
        }));
      },

      completeRun: (kartlaggningId, perspective, responses, summary) => {
        const kartlaggning = get().getKartlaggning(kartlaggningId);
        set((current) => ({
          kartlaggningar: withLog(
            current.kartlaggningar.map((candidate) =>
              candidate.id === kartlaggningId
                ? {
                    ...candidate,
                    runs: candidate.runs.map((run) =>
                      run.perspective === perspective
                        ? { ...run, status: "done" as const, responses, summary, finishedAt: new Date().toISOString() }
                        : run,
                    ),
                  }
                : candidate,
            ),
            kartlaggningId,
            { actor: kartlaggning?.createdBy ?? "okänd", action: "sammanställd", subject: `${perspectiveLabel(perspective)} genomförd` },
          ),
        }));
      },

      setRunProvenance: (kartlaggningId, perspective, values) => {
        set((current) => {
          const actor = values.registeredBy || "okänd";
          const kartlaggningar = current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  runs: candidate.runs.map((run) => (run.perspective === perspective ? { ...run, ...values } : run)),
                }
              : candidate,
          );
          return { kartlaggningar: withLog(kartlaggningar, kartlaggningId, { actor, action: "registrerad", subject: `${perspectiveLabel(perspective)} närvarokänslighet uppdaterad` }) };
        });
      },

      addAction: (kartlaggningId, action) => {
        const id = createId();
        set((current) => {
          const kartlaggningar = current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId ? { ...candidate, actions: [...candidate.actions, { ...action, id }] } : candidate,
          );
          return { kartlaggningar: withLog(kartlaggningar, kartlaggningId, { actor: current.getKartlaggning(kartlaggningId)?.createdBy ?? "okänd", action: "insats tillagd", subject: action.description }) };
        });
      },

      updateAction: (kartlaggningId, actionId, patch) => {
        set((current) => ({
          kartlaggningar: current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  updatedAt: new Date().toISOString(),
                  actions: candidate.actions.map((action) => (action.id === actionId ? { ...action, ...patch } : action)),
                }
              : candidate,
          ),
        }));
      },

      updateActionStatus: (kartlaggningId, actionId, status) => {
        set((current) => ({
          kartlaggningar: current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  updatedAt: new Date().toISOString(),
                  actions: candidate.actions.map((action) => (action.id === actionId ? { ...action, status } : action)),
                }
              : candidate,
          ),
        }));
      },

      removeAction: (kartlaggningId, actionId) => {
        set((current) => ({
          kartlaggningar: current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? { ...candidate, updatedAt: new Date().toISOString(), actions: candidate.actions.filter((action) => action.id !== actionId) }
              : candidate,
          ),
        }));
      },

      addFollowUp: (kartlaggningId, followUp) => {
        set((current) => {
          const kartlaggningar = current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? { ...candidate, followUps: [...candidate.followUps, { ...followUp, id: createId(), at: new Date().toISOString() }] }
              : candidate,
          );
          return {
            kartlaggningar: withLog(kartlaggningar, kartlaggningId, {
              actor: followUp.conductedBy || "okänd",
              action: "uppföljning registrerad",
              subject: `${perspectiveLabel(followUp.perspective, followUp.role)} skattade ${followUp.value}/5`,
            }),
          };
        });
      },

      addAccessLog: (kartlaggningId, entry) => {
        set((current) => ({
          kartlaggningar: withLog(current.kartlaggningar, kartlaggningId, entry),
        }));
      },

      addReusedSessionNote: (kartlaggningId, note) => {
        set((current) => {
          const kartlaggningar = current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId ? { ...candidate, reusedSessionNotes: [...candidate.reusedSessionNotes, note] } : candidate,
          );
          return { kartlaggningar: withLog(kartlaggningar, kartlaggningId, { actor: "professionell", action: "underlag återanvänt", subject: note }) };
        });
      },

      reusePreviousKartlaggning: (kartlaggningId, sourceId) => {
        const kartlaggning = get().getKartlaggning(kartlaggningId);
        const source = get().getKartlaggning(sourceId);
        if (!kartlaggning || !source) throw new Error("Kartläggningen finns inte.");
        if (kartlaggning.participantReference !== source.participantReference) throw new Error("Underlag kan bara återanvändas för samma deltagare.");
        const now = new Date().toISOString();
        const note = `Återanvänt underlag från ${source.title} (${formatDate(source.createdAt)})`;
        set((current) => {
          const kartlaggningar = current.kartlaggningar.map((candidate) =>
            candidate.id === kartlaggningId
              ? {
                  ...candidate,
                  reusedSessionNotes: [...candidate.reusedSessionNotes, note],
                  runs: candidate.runs.map((run) => {
                    const srcRun = source.runs.find((candidateRun) => candidateRun.perspective === run.perspective && candidateRun.status === "done");
                    if (!srcRun) return run;
                    const role = srcRun.role !== undefined ? srcRun.role : run.role;
                    const startedAt = run.startedAt !== undefined ? run.startedAt : srcRun.startedAt;
                    return {
                      ...run,
                      status: "done" as const,
                      ...(role !== undefined ? { role } : {}),
                      conductedBy: srcRun.conductedBy,
                      conductedWith: srcRun.conductedWith,
                      registeredBy: srcRun.registeredBy,
                      ...(startedAt !== undefined ? { startedAt } : {}),
                      finishedAt: now,
                      formVersion: srcRun.formVersion,
                      ...(srcRun.responses !== undefined ? { responses: srcRun.responses } : {}),
                      ...(srcRun.summary !== undefined ? { summary: srcRun.summary } : {}),
                    };
                  }),
                }
              : candidate,
          );
          return { kartlaggningar: withLog(kartlaggningar, kartlaggningId, { actor: kartlaggning.createdBy || "professionell", action: "underlag återanvänt", subject: note }) };
        });
      },

      deleteKartlaggning: (id) => {
        set((current) => ({ kartlaggningar: current.kartlaggningar.filter((kartlaggning) => kartlaggning.id !== id) }));
      },

      resetAll: () => set({ kartlaggningar: [] }),
    }),
    {
      name: "samklang-kartlaggning-v1",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

export { perspectiveLabel };
